/**
 * Marketing consent helpers. A customer's consent records are only ever added to;
 * the latest record per channel is the current status (CON-01 to CON-03).
 */
import type { Channel, ConsentRecord, ConsentSource, ConsentStatus, Customer } from '../../components/customers/data/types';
import { CHANNELS } from '../../components/customers/data/types';

type ConsentHolder = Pick<Customer, 'marketingConsent'>;
type Reachable = Pick<Customer, 'marketingConsent' | 'phone' | 'email' | 'pushEnabled'>;

/** The most recent consent record for a channel, or null when the customer was never asked. */
export function latestConsent(customer: ConsentHolder, channel: Channel): ConsentRecord | null {
  let latest: ConsentRecord | null = null;
  for (const r of customer.marketingConsent) {
    if (r.channel !== channel) continue;
    if (!latest || r.capturedAt >= latest.capturedAt) latest = r;
  }
  return latest;
}

export function consentStatus(customer: ConsentHolder, channel: Channel): ConsentStatus | null {
  return latestConsent(customer, channel)?.status ?? null;
}

/** The customer has a phone number, an email address or a device the channel can deliver to. */
export function hasAddress(customer: Pick<Customer, 'phone' | 'email' | 'pushEnabled'>, channel: Channel): boolean {
  if (channel === 'sms') return Boolean(customer.phone);
  if (channel === 'email') return Boolean(customer.email);
  return customer.pushEnabled;
}

/** CON-01 to CON-03 — granted marketing consent plus somewhere to deliver to. */
export function isReachable(customer: Reachable, channel: Channel): boolean {
  return hasAddress(customer, channel) && consentStatus(customer, channel) === 'granted';
}

export function reachableChannels(customer: Reachable): Channel[] {
  return CHANNELS.filter((c) => isReachable(customer, c));
}

/** What to say about one channel for one customer. */
export function consentLabel(customer: Reachable, channel: Channel): string {
  const status = consentStatus(customer, channel);
  if (status === 'revoked') return 'Opted out';
  if (status === 'granted') return hasAddress(customer, channel) ? 'Subscribed' : 'Subscribed, no address';
  return 'No consent';
}

/** Wording version shown to customers today; recorded on every new consent record. */
export const CURRENT_CONSENT_TEXT_VERSION = 'v1.1';

/**
 * CON-12 — an opt-out recorded for the customer by staff or by an inbound message.
 * There is deliberately no counterpart that grants consent: only the customer can subscribe (CON-13).
 */
export function buildOptOutRecord(id: string, channel: Channel, source: ConsentSource, capturedBy: string | null, at: string): ConsentRecord {
  return {
    id, channel, status: 'revoked', source,
    consentTextVersion: CURRENT_CONSENT_TEXT_VERSION,
    capturedAt: at, capturedBy, createdAt: at, updatedAt: at,
  };
}

/**
 * LNK-06, LNK-07 — merged profiles keep every consent record from all of them, in time order,
 * so the most recent record across the profiles decides each channel.
 */
export function mergeConsent(customers: ConsentHolder[]): ConsentRecord[] {
  const seen = new Set<string>();
  return customers
    .flatMap((c) => c.marketingConsent)
    .filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)))
    .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
}
